import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Globe, Plus, Check, Clock, Copy, Trash2 } from 'lucide-react';

export default function DomainsSettings() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDomains();
  }, [orgId]);

  const fetchDomains = async () => {
    try {
      const response = await apiClient.get(`/domains/orgs/${orgId}`);
      setDomains(response.data);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    try {
      await apiClient.post(`/domains/orgs/${orgId}`, { domain: newDomain });
      toast.success('Domain added. Add the TXT record to verify.');
      setNewDomain('');
      setDialogOpen(false);
      fetchDomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    }
  };

  const handleVerify = async (domainId) => {
    try {
      await apiClient.post(`/domains/orgs/${orgId}/${domainId}/verify`);
      toast.success('Domain verified (simulated)');
      fetchDomains();
    } catch (error) {
      toast.error('Verification failed');
    }
  };

  const handleDelete = async (domainId) => {
    if (!confirm('Are you sure you want to remove this domain?')) return;

    try {
      await apiClient.delete(`/domains/orgs/${orgId}/${domainId}`);
      toast.success('Domain removed');
      fetchDomains();
    } catch (error) {
      toast.error('Failed to remove domain');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/enterprise/orgs/${orgId}/settings`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">Domain Management</h1>
            <p className="text-gray-600 mt-1">Verify domains for your organization</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Domain
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Domain</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Domain Name *</Label>
                <Input
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="company.com"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter your domain without http:// or www
                </p>
              </div>
              <Button onClick={handleAddDomain} disabled={!newDomain} className="w-full">
                Add Domain
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <Card className="p-12 rounded-lg text-center">
          <p className="text-gray-500">Loading domains...</p>
        </Card>
      ) : domains.length > 0 ? (
        <div className="space-y-4">
          {domains.map((domain) => (
            <Card key={domain.id} className="p-6 rounded-lg">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Globe className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-gray-900">{domain.domain}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {domain.status === 'verified' ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-success/10 text-success flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-warning/10 text-warning flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                      {domain.verifiedAt && (
                        <span className="text-xs text-gray-500">
                          {new Date(domain.verifiedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(domain.id)}>
                  <Trash2 className="w-4 h-4 text-gray-400 hover:text-error" />
                </Button>
              </div>

              {domain.status === 'pending' && (
                <div className="space-y-3">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-900">TXT Record</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(domain.txtRecord)}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <code className="text-xs font-mono text-gray-700 break-all">
                      {domain.txtRecord}
                    </code>
                  </div>
                  <p className="text-sm text-gray-600">
                    Add this TXT record to your DNS settings, then click Verify.
                  </p>
                  <Button onClick={() => handleVerify(domain.id)} size="sm">
                    Verify Domain
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 rounded-lg text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
            No domains yet
          </h3>
          <p className="text-gray-600 mb-6">
            Add and verify domains to restrict membership and enable domain-based policies
          </p>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add First Domain
          </Button>
        </Card>
      )}
    </div>
  );
}
